import {
  AlertIcon,
  CurecallButton,
  CurecallModalContainer,
  CurecallTooltip,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
  LoadingSpinner,
} from '@honestica/curecall-ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminCommunicationChannelService } from 'src/services/admin-communication-channel.service'
import { EChannelStatusTag, EChannelType } from 'src/types/communication-channel'
import type { TChannelStatusDto, TSubjectType } from 'src/types/communication-channel'
import { getErrorMessage } from 'src/utils/http.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { useNotification } from 'src/providers/notification.provider'

type TOverrideChannelModalProps = {
  subjectType: TSubjectType
  subjectId: number
  onClose: () => void
  onSuccess: () => void
}

function getChannelScopeLabel(
  channel: TChannelStatusDto,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (channel.ProjectId !== null)
    return t('components.override-channel-modal.scope-project', { id: channel.ProjectId })
  if (channel.OrganizationId !== null)
    return t('components.override-channel-modal.scope-organisation', { id: channel.OrganizationId })
  return t('components.override-channel-modal.scope-global')
}

function ChannelItem({
  channel,
  isSelected,
  onSelect,
}: {
  channel: TChannelStatusDto
  isSelected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const isCurrentlyAssigned = channel.status === EChannelStatusTag.ASSIGNED_TO_SUBJECT

  return (
    <div
      onClick={isCurrentlyAssigned ? undefined : onSelect}
      className={twMerge(
        'c-flex c-flex-col c-gap-1 c-rounded c-border c-p-3 c-text-sm c-transition-colors',
        isCurrentlyAssigned
          ? 'c-border-blue-200 c-bg-blue-50 c-cursor-default'
          : 'c-cursor-pointer hover:c-bg-grey-50',
        isSelected && 'c-border-blue-400 c-bg-blue-50',
        !isSelected && !isCurrentlyAssigned && 'c-border-grey-200',
      )}
    >
      <div className="c-flex c-items-center c-justify-between c-gap-2">
        <div className="c-flex c-items-center c-gap-2">
          <span className="c-font-medium">{channel.phone}</span>
          <span className="c-rounded c-bg-grey-100 c-px-1.5 c-py-0.5 c-text-xs c-text-grey-600">
            {channel.type}
          </span>
          <span className="c-text-xs c-text-grey-500">{channel.provider}</span>
          <span className="c-rounded c-border c-border-grey-200 c-px-1.5 c-py-0.5 c-text-xs c-text-grey-600">
            {getChannelScopeLabel(channel, t)}
          </span>
          {channel.type === EChannelType.RCS && channel.settings.displayedName && (
            <span className="c-text-xs c-text-grey-500">
              {t('components.override-channel-modal.displayed-name', {
                displayedName: channel.settings.displayedName,
              })}
            </span>
          )}
        </div>
        {channel.cordon && (
          <CurecallTooltip
            content={t('components.override-channel-modal.cordon-warning')}
            tooltipPosition="bottom-end"
          >
            <AlertIcon className="c-h-4 c-w-4 c-text-orange-400" />
          </CurecallTooltip>
        )}
        {!channel.isInSubjectScope && (
          <span className="c-rounded c-bg-orange-100 c-px-1.5 c-py-0.5 c-text-xs c-text-orange-700">
            {t('components.override-channel-modal.out-of-scope-warning')}
          </span>
        )}
      </div>

      {(channel.status === EChannelStatusTag.USED_BY_PATIENT ||
        channel.status === EChannelStatusTag.USED_BY_HR_CONTACT) &&
        channel.conflictingHolder && (
          <p className="c-text-xs c-text-orange-700">
            {t('components.override-channel-modal.displacement-warning', {
              type: channel.conflictingHolder.type,
              id: channel.conflictingHolder.id,
              projectId: channel.conflictingHolder.projectId,
            })}
          </p>
        )}
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <p className="c-text-xs c-font-semibold c-uppercase c-tracking-wide c-text-grey-700">{children}</p>
}

function AdvancedSection({
  label,
  show,
  onToggle,
  children,
}: {
  label: string
  show: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="c-flex c-flex-col c-gap-2">
      <button
        onClick={onToggle}
        className="c-text-left c-text-xs c-text-grey-600 c-underline c-underline-offset-2"
      >
        {label}
      </button>
      {show && <div className="c-flex c-flex-col c-gap-2">{children}</div>}
    </div>
  )
}

export function OverrideChannelModal({
  subjectType,
  subjectId,
  onClose,
  onSuccess,
}: TOverrideChannelModalProps) {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null)
  const [showOutOfScope, setShowOutOfScope] = useState(false)
  const [showCordon, setShowCordon] = useState(false)
  const [showIncompatible, setShowIncompatible] = useState(false)

  const { data: channels, isLoading } = useQuery({
    queryKey: ['admin-channels-available', subjectType, subjectId],
    queryFn: () => AdminCommunicationChannelService.getAvailableChannels(subjectType, subjectId),
  })

  const assignMutation = useMutation({
    mutationFn: () =>
      AdminCommunicationChannelService.assignChannel(selectedChannelId!, {
        type: subjectType,
        id: subjectId,
      }),
    onSuccess: () => {
      onSuccess()
      onClose()
    },
    onError: (error) => {
      notify({
        title: t('components.override-channel-modal.error-title'),
        message: getErrorMessage(error),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
  })

  const current = channels?.filter((c) => c.status === EChannelStatusTag.ASSIGNED_TO_SUBJECT) ?? []

  // Non-current channels are split into mutually exclusive buckets:
  // out-of-scope takes priority, then cordon, then incompatible, then main.
  const nonCurrent = channels?.filter((c) => c.status !== EChannelStatusTag.ASSIGNED_TO_SUBJECT) ?? []
  const outOfScope = nonCurrent.filter((c) => !c.isInSubjectScope)
  const cordon = nonCurrent.filter((c) => c.isInSubjectScope && c.cordon)
  const incompatible = nonCurrent.filter((c) => c.isInSubjectScope && !c.cordon && !c.isPhoneCompatible)
  const main = nonCurrent.filter((c) => c.isInSubjectScope && !c.cordon && c.isPhoneCompatible)

  const freeInScope = main.filter((c) => c.status === EChannelStatusTag.FREE)
  const occupiedInScope = main.filter(
    (c) =>
      c.status === EChannelStatusTag.USED_BY_PATIENT || c.status === EChannelStatusTag.USED_BY_HR_CONTACT,
  )

  const selectedChannel = channels?.find((c) => c.id === selectedChannelId)
  const canAssign = selectedChannelId !== null && selectedChannel?.status !== 'ASSIGNED_TO_SUBJECT'

  return (
    <CurecallModalContainer
      title={t('components.override-channel-modal.title')}
      isOpen={true}
      toggle={onClose}
      closeModalAriaLabel={t('common.close')}
      dialogClassName="c-w-[900px]"
    >
      <div className="c-flex c-flex-col c-gap-4 c-min-h-[200px]">
        {isLoading && (
          <div className="c-flex c-flex-1 c-items-center c-justify-center">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && channels && (
          <div className="c-flex c-flex-col c-gap-4">
            {current.length > 0 && (
              <div className="c-flex c-flex-col c-gap-2">
                <SectionTitle>{t('components.override-channel-modal.current-section')}</SectionTitle>
                {current.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </div>
            )}

            {freeInScope.length > 0 && (
              <div className="c-flex c-flex-col c-gap-2">
                <SectionTitle>{t('components.override-channel-modal.free-section')}</SectionTitle>
                {freeInScope.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </div>
            )}

            {occupiedInScope.length > 0 && (
              <div className="c-flex c-flex-col c-gap-2">
                <SectionTitle>{t('components.override-channel-modal.occupied-section')}</SectionTitle>
                {occupiedInScope.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </div>
            )}

            {outOfScope.length > 0 && (
              <AdvancedSection
                label={t('components.override-channel-modal.show-out-of-scope')}
                show={showOutOfScope}
                onToggle={() => setShowOutOfScope((v) => !v)}
              >
                {outOfScope.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </AdvancedSection>
            )}

            {cordon.length > 0 && (
              <AdvancedSection
                label={t('components.override-channel-modal.show-cordon')}
                show={showCordon}
                onToggle={() => setShowCordon((v) => !v)}
              >
                {cordon.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </AdvancedSection>
            )}

            {incompatible.length > 0 && (
              <AdvancedSection
                label={t('components.override-channel-modal.show-incompatible')}
                show={showIncompatible}
                onToggle={() => setShowIncompatible((v) => !v)}
              >
                {incompatible.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isSelected={selectedChannelId === channel.id}
                    onSelect={() => setSelectedChannelId(channel.id)}
                  />
                ))}
              </AdvancedSection>
            )}
          </div>
        )}

        <div className="c-flex c-justify-end c-pt-2">
          <CurecallButton
            appearance={ECurecallButtonAppearance.PRIMARY}
            size={ECurecallSize.SMALL}
            onClick={() => assignMutation.mutate()}
            disabled={!canAssign || assignMutation.isPending}
          >
            {t('components.override-channel-modal.assign-button')}
          </CurecallButton>
        </div>
      </div>
    </CurecallModalContainer>
  )
}
